import {
    Component,
    OnInit,
    OnDestroy,
    Input,
    Output,
    EventEmitter
} from '@angular/core';
import {FormGroup, FormControl, Validators, AbstractControl} from '@angular/forms';
import { CustomerSite } from '@shared/models/customer-site';
import { Customer } from '@shared/models/customer';

import { BehaviorSubject, Subscription, of, map, switchMap, take, combineLatest } from 'rxjs';

import { Message, SelectItem } from 'primeng/api';
import { MessageService } from '@shared/services/message-service';

import {environment} from '@environment';

import { OutboundLoadService } from '../../../../services/outbound-load.service';
import { ShiftContextService } from '../../../../services/shift-context.service';
import {CustomerService} from '@shared/services/customer.service';

import { OutboundLoadPartialUpdate } from '../../../../models/outbound-load-partial-update';
import {OutboundLoad, OutboundLoadPartial} from '../../../../models/outbound-load';
import { WorkedShift } from '@shared/models/worked-shift';
import {CUST_REF_PATTERN} from "../../form/form.component";
import { DateTime } from 'luxon';
import { SiteConfig } from '@shared/models/site-config';
import {ShiftContext} from "../../../../models/shift-context";
import {timeFormat} from "@shared/helpers/timeFormat";
import {filter} from "rxjs/operators";
import { toTimestamp } from '../../../../helpers/to-timestamp';

@Component({
    selector: 'ship-load',
    templateUrl: './ship-load.component.html',
    styleUrls: ['./ship-load.component.scss']
})
export class ShipLoadComponent implements OnInit, OnDestroy {
    static readonly validationMessage: Message = {
        severity: 'warn',
        summary: 'Could Not Ship Load',
        detail: 'Please correct the listed errors.'
    };

    static readonly successShippedMessage: Message = {
        severity: 'success',
        summary: 'Load Shipped',
        detail: 'Load shipped successfully.'
    };

    static readonly successSavedMessage: Message = {
        severity: 'success',
        summary: 'Load Updated',
        detail: 'Load updated successfully.'
    };

    private _show = new BehaviorSubject<boolean>(false);

    @Input('show') set show(val: boolean) {
        this._show.next(val);
    }

    get show(): boolean {
        return this._show.value;
    }

    @Output() showChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    _load = new BehaviorSubject<OutboundLoad>(null);

    @Input() set load(val: OutboundLoad) {
        this._load.next(val);
    }

    @Output() loadUpdated: EventEmitter<OutboundLoadPartial> = new EventEmitter<OutboundLoadPartial>();

    loading: boolean = false;

    saving: boolean = false;

    customerReferenceRequired = false;

    displayAddShipToDialog: boolean = false;

    shipLoad: boolean = false;

    alreadyShipped: boolean = false;

    form = new FormGroup({
        shippedShiftId: new FormControl<number>(null),
        dateShipped: new FormControl<Date>(null),
        shippedTime: new FormControl<string>(null),
        sealNumber: new FormControl<string>(''),
        customerReferenceNumber: new FormControl<string>(null),
        customerSiteId: new FormControl<number>(null)
    });

    selectedWorkedShift: WorkedShift;

    selectedCustomer: Customer;
    selectedPooler: Customer;
    poolerSites: CustomerSite[];
    poolerSiteSelects: SelectItem[];

    cfGlidRequired: boolean;
    siteTimezone: string;

    subscriptions: Subscription[] = [];
    activeIndex: number = null;

    private context: ShiftContext | null = null;

    constructor(
        private loadService: OutboundLoadService,
        private shiftContextService: ShiftContextService,
        private messageService: MessageService,
        private customerService: CustomerService) { }

    ngOnInit() {
        this.subscriptions = [
            combineLatest([
                this.shiftContextService.SiteConfig,
                this._load
            ]).subscribe(([configs, load]) => {
                this.siteTimezone = configs
                    .find(c => c.configKey === SiteConfig.SITE_TIME_ZONE)
                    ?.GetValue<string>()
                    ?? environment.fallbackTimeZone;
            }),

            this._show.subscribe(show => {
                this.showChange.emit(show);
            }),

            combineLatest([
                this._load,
                this._show
            ]).pipe(
                filter(([load, show]) => !!load && show),
                switchMap(() =>
                    this.shiftContextService.CurrentContext.pipe(take(1))
                )
            ).subscribe(context => {
                this.context = context;
                this.SetLoad();
            })
        ];
        this.updateFormState();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
        this._load.next(null);
    }

    SetLoad() {
        const load = this._load.value;

        if (load) {
            this.form.controls.sealNumber.setValue(load.sealNumber);
            this.form.controls.customerReferenceNumber.setValue(load.customerReferenceNumber);

            this.form.controls.shippedShiftId.setValue(load.shippedWorkedShiftId ?? this.context?.id);
            this.form.controls.dateShipped.setValue(load.shippedTimestamp?.startOf('day').toJSDate() ?? this.context?.date?.toJSDate());
            this.form.controls.shippedTime.setValue(load.shippedTimestamp ? timeFormat(load.shippedTimestamp?.setZone(this.siteTimezone)) : null);

            this.alreadyShipped = !!load.shippedWorkedShiftId;
            this.shipLoad = !!load.shippedWorkedShiftId;
            this.activeIndex = this.shipLoad ? 0 : null;
            this.updateFormState();
            this.customerService.getCustomers(load.siteId, load.loadedWorkedShift.date, 'outbound').subscribe(customerAssignments => {
                this.selectedCustomer = customerAssignments.customers.find(s => s.id === load.customerId);
                if (this.selectedCustomer)
                    this.cfGlidRequired = this.selectedCustomer.accountNumber === environment.ids.ChepCustomerAccountNumber;

                this.form.controls.customerReferenceNumber.updateValueAndValidity();
                this.selectedPooler = customerAssignments.customers
                    .find(c => c.id === load.poolerId);

                this.poolerSites = this.selectedPooler?.customerSites || [];
                this.poolerSiteSelects = [
                    { value: null, label: '-- New Site --' },
                    ...this.poolerSites.map(s => ({
                        value: s.id,
                        label: s.chepGlobalLocationId ? `${s.chepGlobalLocationId} - ${s.partySiteName}` : s.partySiteName,
                        styleClass: s.activeFlag === false ? 'text-strikethrough' : ''
                    }))
                ];

                this.customerReferenceRequired = load.customer.isCustomerRefRequired || this.selectedPooler?.isCustomerRefRequired;

                this.form.controls.customerReferenceNumber.setAsyncValidators(control => {
                    const outboundLoad = this._load.value;

                    if (!outboundLoad?.customerId || !control.value)
                        return Promise.resolve(null);

                    return this.loadService.refNumberInUse(outboundLoad.customerId, control.value)
                    .pipe(map(inUse => {
                        if (!!inUse && outboundLoad.id !== inUse)
                            return { numberInUse: { valid: false } };
                        else
                            return null;
                    }));
                });

                this.form.controls.customerSiteId.setValue(load.customerSiteId);
            });
        }
        else {
            this.form.controls.sealNumber.setValue('');
            this.form.controls.customerReferenceNumber.setValue('');
        }
    }

    showDialogToAddShipTo() {
        this.displayAddShipToDialog = true;
    }

    closeAddShippedFromDialog(dialogClosed: boolean) {
        this.displayAddShipToDialog = dialogClosed;
    }

    getPoolerSite(): CustomerSite {
        return this.poolerSites?.find(s => s.id === this.form.controls.customerSiteId.value);
    }

    customerSiteAdded(newSite: CustomerSite): void {
        this.selectedPooler.customerSites = [...this.selectedPooler.customerSites, newSite];
        this.poolerSites = [...this.poolerSites, newSite];
        this.poolerSiteSelects = [...this.poolerSiteSelects, {
            value: newSite.id,
            label: newSite.chepGlobalLocationId ? `${newSite.chepGlobalLocationId} - ${newSite.partySiteName}` : newSite.partySiteName
        }];

        this.form.controls.customerSiteId.setValue(newSite.id);
    }

    Close() {
        this.show = false;
        this.shipLoad = false;
    }

    onShippedShiftDateChanged(date: DateTime) {
        if (date)
            this.form.controls.dateShipped.setValue(date.toJSDate());
    }

    WorkedShiftSelected(shift: WorkedShift) {
        this.selectedWorkedShift = shift;
    }

    SetShipLoad(val: boolean) {
        this.shipLoad = val;
        this.updateFormState();
    }

    SaveLoad() {
        if (!this.form.valid) {
            this.form.markAllAsTouched();

            this.messageService.add(ShipLoadComponent.validationMessage);
            return;
        }

        this.saving = true;

        const update: OutboundLoadPartialUpdate = {
            id: this._load.value.id,
            sealNumber: this.form.controls.sealNumber.value,
            customerReferenceNumber: this.form.controls.customerReferenceNumber.value,
            customerSiteId: this.form.controls.customerSiteId.value,
            customerSite: this.poolerSites.find(s => s.id === this.form.controls.customerSiteId.value),
            shippedWorkedShift: null,
            shippedWorkedShiftId: null,
            shippedTimestamp: null
        };

        if (this.shipLoad) {
            update.shippedWorkedShift = this.selectedWorkedShift;
            update.shippedWorkedShiftId = this.form.controls.shippedShiftId.value;
            update.shippedTimestamp = toTimestamp(this.form.controls.dateShipped.value, this.form.controls.shippedTime.value, this.siteTimezone);
        }

        this.loadService.loadGridUpdate(update).subscribe({
            next: ol => {
                this.loadUpdated.emit(ol);
                if (this.shipLoad) {
                    this.messageService.add(ShipLoadComponent.successShippedMessage);
                } else
                    this.messageService.add(ShipLoadComponent.successSavedMessage);
                this.show = false;
                this.saving = false;
            },
            error: _err => {
                this.saving = false;
            }
        });
    }

    updateFormState() {
        this.form.controls.sealNumber.clearValidators();
        this.form.controls.customerSiteId.clearValidators();
        this.form.controls.customerReferenceNumber.clearValidators();
        this.form.controls.shippedShiftId.clearValidators();
        this.form.controls.shippedTime.clearValidators();
        this.form.controls.dateShipped.clearValidators();

        if (this.shipLoad) {
            this.form.controls.sealNumber.setValidators([Validators.required]);
            this.form.controls.customerSiteId.setValidators([(control: AbstractControl): { [key: string]: any } | null => {
                if (this.selectedCustomer?.isShipToAddressRequired && this.shipLoad) {
                    const customerSite = this.getPoolerSite();
                    const hasAddress = customerSite?.address1?.length > 0;
                    return hasAddress ? null : {noAddress: {value: control.value}};
                }
                return null;
            }]);

            this.form.controls.shippedShiftId.setValidators([Validators.required]);
            this.form.controls.shippedTime.setValidators([Validators.required]);
            this.form.controls.dateShipped.setValidators([Validators.required]);

            if (this.customerReferenceRequired)
                this.form.controls.customerReferenceNumber.setValidators([Validators.required]);
        }

        if (this.alreadyShipped) {
            this.form.controls.customerReferenceNumber.disable();
            this.form.controls.sealNumber.disable();
            this.form.controls.customerSiteId.disable();
        }
        else {
            this.form.controls.customerReferenceNumber.enable();
            this.form.controls.sealNumber.enable();
            this.form.controls.customerSiteId.enable();
        }

        this.form.controls.customerReferenceNumber.addValidators([Validators.pattern(CUST_REF_PATTERN)]);
        this.form.controls.sealNumber.updateValueAndValidity();
        this.form.controls.customerSiteId.updateValueAndValidity();
        this.form.controls.customerReferenceNumber.updateValueAndValidity();
        this.form.controls.shippedShiftId.updateValueAndValidity();
        this.form.controls.shippedTime.updateValueAndValidity();
        this.form.controls.dateShipped.updateValueAndValidity();
    }
}
